/**
 * TenantProvider — carrega o tenant atual a partir do subdominio
 * e busca configurações (logo, cores, nome) em tab_tenants.
 *
 * Aplica CSS variables no :root para tema dinamico.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { supabase, tenantCodeAtual } from './supabase';

export interface TenantInfo {
  seq_tenant: number;
  cod_tenant: string;
  nom_tenant: string;
  des_logo_url: string | null;
  cod_cor_primaria: string | null;
  cod_cor_secundaria: string | null;
  cod_cor_acento: string | null;
  des_favicon_url: string | null;
  des_email_suporte: string | null;
  num_whatsapp_suporte: string | null;
  des_url_termos: string | null;
  des_url_pol_priv: string | null;
  cod_dpo: string | null;
  des_email_dpo: string | null;
}

interface TenantContextValue {
  tenant: TenantInfo | null;
  carregando: boolean;
  erro: string | null;
  recarregar: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

async function carregarTenantPorCodigo(cod: string | null): Promise<TenantInfo | null> {
  if (!cod) return null;
  const { data, error } = await supabase
    .from('tab_tenants')
    .select(
      'seq_tenant, cod_tenant, nom_tenant, des_logo_url, cod_cor_primaria, cod_cor_secundaria, cod_cor_acento, des_favicon_url, des_email_suporte, num_whatsapp_suporte, des_url_termos, des_url_pol_priv, cod_dpo, des_email_dpo',
    )
    .eq('cod_tenant', cod)
    .maybeSingle();

  if (error) {
    // tab_tenants pode ainda não existir antes da migration multi-tenant
    if (error.code === '42P01') return null;
    throw error;
  }
  return (data as TenantInfo) ?? null;
}

function aplicarTema(t: TenantInfo) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (t.cod_cor_primaria) root.style.setProperty('--primary-hex', t.cod_cor_primaria);
  if (t.cod_cor_secundaria) root.style.setProperty('--secondary-hex', t.cod_cor_secundaria);
  if (t.cod_cor_acento) root.style.setProperty('--accent-hex', t.cod_cor_acento);
  if (t.des_favicon_url) {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = t.des_favicon_url;
  }
  if (t.nom_tenant) document.title = `${t.nom_tenant} | Painel`;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      const t = await carregarTenantPorCodigo(tenantCodeAtual);
      if (t) {
        setTenant(t);
        aplicarTema(t);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void recarregar();
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, carregando, erro, recarregar }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant deve ser usado dentro de TenantProvider');
  return ctx;
}
